import {Stack} from "@src/CommonComponents/Stack/Stack";
import styles from "./eventRequest.module.less";

import {useCallback, useEffect, useMemo, useState} from "react";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {Gt} from "@src/i18n/Gt.tsx";
import {useTranslation} from "react-i18next";
import cn from "classnames";
import {useNavigate} from "react-router-dom";
import {useMutation, useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {apiClient} from "@src/httpClient/api.ts";
import {useRequestAction} from "../useRequestAction.ts"
import {
    EventRequest as EventRequestType,
    EventRequestRequest,
    WorkEventKindEnum as EventKindEnum,
    RequestStatusEnum, Branch,
} from "@src/httpClient/__generated__/Api.ts";
import {queryClient} from "@src/QueryClient/queryClient.ts";
import {useParams} from "react-router";
import {useEntityQuery} from "@src/httpClient/utils/queryHelpers.ts";
import {Body_14_Coral_Empasis_14, Body_14_Coral_Primary_Regular_14} from "@UI/typography/generated/typography.tsx";
import {TextInput} from "@src/CommonComponents/Input/TextInput";
import {formatDate} from "@src/utils/formatDate.ts";
import {Textarea} from "@src/CommonComponents/Input/Textarea.tsx";
import {useCredentialsStore} from "@src/Store/userStore.ts";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {AvatarWithInfo} from "@src/Pages/SettingsPage/MainMenu.tsx";
import {Checked} from "@UI/components/icons/Checked.tsx";
import {Trash} from "@UI/components/icons/Trash.tsx";
import {Xcross} from "@UI/components/icons/Xcross.tsx";
import {useErrorStore} from "@src/Layers/ErrorLayer/ErrorStore.ts";
import {BackButtonRow} from "@src/CommonComponents/HeaderWithBackButton/HeaderWithBackButton.tsx";
import {FilterDropdown} from "@src/CommonComponents/FilterSelect/FilterDropdown/FilterDropdown.tsx";

const eventKindLabels: Record<EventKindEnum, string> = {
    [EventKindEnum.In]: 'Clock In',
    [EventKindEnum.Out]: 'Clock Out',
}

const EventKindSelector = ({value, onChange, disabled}: {
    value: EventKindEnum,
    onChange: (v: EventKindEnum) => void,
    disabled?: boolean,
}) => {
    return (
        <div className={styles.typeSelector}>
            {Object.values(EventKindEnum).map(kind => (
                <Button
                    key={kind}
                    type={value === kind ? ButtonType.secondary : ButtonType.third}
                    className={cn(styles.typeChip, {[styles.typeChipActive]: value === kind})}
                    onClick={() => !disabled && onChange(kind)}
                    disabled={disabled}
                >
                    <Gt>{eventKindLabels[kind]}</Gt>
                </Button>
            ))}
        </div>
    )
}

interface FormState {
    recorded_at: string
    reason: string
}

const emptyForm: FormState = {
    recorded_at: '',
    reason: '',
}

export const EventRequest = () => {
    const {requestId} = useParams<{ requestId: string }>()
    const navigate = useNavigate()
    const employee = useCredentialsStore(x => x.employee)
    const {t} = useTranslation()
    const setAppError = useErrorStore(x => x.setAppError)

    const isViewMode = !!requestId
    const isManager = !!employee?.is_manager
    const {actionInFlight, runAction} = useRequestAction("eventRequest", "/requests/?tab=event")
    const [rejectReason, setRejectReason] = useState('')
    const [eventKind, setEventKind] = useState<EventKindEnum>(EventKindEnum.In)
    const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null)
    const [form, setForm] = useState<FormState>(emptyForm)
    const [errors, setErrors] = useState<Record<string, string>>({})

    const branches = employee?.branches ?? []

    // Load full branch data for geolocation
    const {data: branchData} = useQuery<Branch>({
        queryKey: ['branch-detail', selectedBranchId],
        queryFn: async () => {
            const r = await apiClient.api.companyBranchesRetrieve(selectedBranchId!)
            return r.data
        },
        enabled: !!selectedBranchId,
    })

    const branchOptions = useMemo(() =>
        branches.map(b => ({label: b.name, value: String(b.id)})),
    [branches])

    // Auto-select first branch
    useEffect(() => {
        if (!selectedBranchId && branches.length > 0) {
            setSelectedBranchId(branches[0].id)
        }
    }, [branches])

    // Load existing request
    const {data} = useEntityQuery<EventRequestType>(
        ["eventRequest", requestId],
        async () => await apiClient.api.workflowEventRequestsRetrieve(Number(requestId))
    )

    const isOwnRequest = !isViewMode || (data?.employee === employee?.id)
    const isManagerReview = isViewMode && isManager && !isOwnRequest

    // Populate form from loaded data
    useEffect(() => {
        if (!data) return
        setEventKind(data.event_kind ?? EventKindEnum.In)
        setForm({
            recorded_at: data.recorded_at ? data.recorded_at.slice(0, 16) : '',
            reason: data.reason ?? '',
        })
    }, [data])

    const onChange = useCallback((field: keyof FormState, value: string) => {
        setForm(prev => ({...prev, [field]: value}))
        setErrors(prev => {
            const next = {...prev}
            delete next[field]
            return next
        })
    }, [])

    const validate = (): boolean => {
        const e: Record<string, string> = {}
        if (!form.recorded_at) e.recorded_at = 'Required'
        if (!form.reason) e.reason = 'Required'
        if (!selectedBranchId) e.branch = 'Required'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const {mutate, isPending} = useMutation<EventRequestType, AxiosError>({
        mutationKey: ["eventRequest", "create"],
        mutationFn: async () => {
            const latitude = branchData?.location_latitude ?? null
            const longitude = branchData?.location_longitude ?? null

            const payload: EventRequestRequest = {
                employee: employee!.id,
                event_kind: eventKind,
                recorded_at: new Date(form.recorded_at).toISOString(),
                longitude,
                latitude,
                branch_id: selectedBranchId,
                reason: form.reason,
            }
            const result = await apiClient.api.workflowEventRequestsCreate(payload)
            return result.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["eventRequest"]})
            queryClient.invalidateQueries({queryKey: ["requests-list"]})
            navigate("/requests/?tab=event")
        },
        onError: (error: AxiosError) => {
            setAppError({
                axiosError: error,
                message: (error.response?.data as any)?.detail ?? error.message,
                showModal: true,
                retry: async () => mutate(),
            })
        },
    })

    const isFormValid = useMemo(() => {
        return !!(form.recorded_at && form.reason && selectedBranchId)
    }, [form, selectedBranchId])

    const handleSubmit = useCallback(() => {
        if (!validate()) return
        mutate()
    }, [form, eventKind, selectedBranchId, branchData])

    return (
        <Stack direction={"column"} gap={16} className={styles.subMenuPage}>
            <BackButtonRow title={"Back"} backLink={"/requests/?tab=event"} hideOnMobile/>
            <AvatarWithInfo employee={isViewMode && data ? {id: data.employee, first_name: data.employee_name} as any : employee}/>

            <Stack direction={"column"} className={styles.whiteBlock} justifyContent={"center"} gap={24}>
                <Stack direction={"column"} gap={6}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>Event kind</Gt>
                    </Body_14_Coral_Empasis_14>
                    <EventKindSelector
                        value={eventKind}
                        onChange={setEventKind}
                        disabled={isViewMode}
                    />
                </Stack>
            </Stack>

            <Stack direction={"column"} className={styles.whiteBlock} justifyContent={"center"} gap={24}>
                {!isViewMode && (
                    <Stack direction={"column"} gap={6}>
                        <Body_14_Coral_Empasis_14>
                            <Gt>Branch</Gt>
                        </Body_14_Coral_Empasis_14>
                        <FilterDropdown
                            options={branchOptions}
                            currentValue={String(selectedBranchId ?? '')}
                            onChange={(v) => setSelectedBranchId(Number(v))}
                        />
                        {errors.branch && (
                            <Body_14_Coral_Primary_Regular_14 style={{color: 'var(--coral-special-error-red)'}}>
                                <Gt>{errors.branch}</Gt>
                            </Body_14_Coral_Primary_Regular_14>
                        )}
                    </Stack>
                )}

                <Stack direction={"column"} gap={6}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>Date and time</Gt>
                    </Body_14_Coral_Empasis_14>
                    <TextInput
                        name="recorded_at"
                        value={form.recorded_at}
                        onChange={v => onChange('recorded_at', v)}
                        type="datetime-local"
                        // Any time today or earlier, matching the backend rule
                        max={`${formatDate(new Date().toString())}T23:59`}
                        hasError={!!errors.recorded_at}
                        disabled={isViewMode}
                    />
                </Stack>
            </Stack>

            <Stack direction={"column"} className={styles.whiteBlock} gap={24}>
                <Body_14_Coral_Empasis_14>
                    <Gt>Reason</Gt>
                </Body_14_Coral_Empasis_14>
                <Textarea
                    name="reason"
                    initialValue={form.reason}
                    onValueChange={v => onChange('reason', v as string)}
                    disabled={isViewMode}
                />
            </Stack>

            {isViewMode && data?.status === RequestStatusEnum.Rejected && (
                <Stack direction={"column"} className={styles.whiteBlock} gap={12}>
                    <Stack direction={"column"} gap={6}>
                        <Body_14_Coral_Empasis_14>
                            <Gt>Rejected by</Gt>
                        </Body_14_Coral_Empasis_14>
                        <Body_14_Coral_Primary_Regular_14>
                            {data.reviewed_by_name}
                        </Body_14_Coral_Primary_Regular_14>
                    </Stack>
                    {data.reject_reason && (
                        <Stack direction={"column"} gap={6}>
                            <Body_14_Coral_Empasis_14>
                                <Gt>Rejection reason</Gt>
                            </Body_14_Coral_Empasis_14>
                            <Body_14_Coral_Primary_Regular_14>
                                {data.reject_reason}
                            </Body_14_Coral_Primary_Regular_14>
                        </Stack>
                    )}
                </Stack>
            )}

            {!isViewMode && (
                <Button
                    icon={<Checked/>}
                    type={ButtonType.secondary}
                    color={ColorNames.CORAL_GREEN}
                    className={styles.submitButton}
                    onClick={handleSubmit}
                    disabled={isPending || !employee?.id || !isFormValid}
                >
                    <Gt>Submit application</Gt>
                </Button>
            )}

            {isViewMode && data?.status === RequestStatusEnum.Pending && isOwnRequest && (
                <Button
                    icon={<Trash/>}
                    type={ButtonType.secondary}
                    color={ColorNames.CORAL_FIRE}
                    className={styles.submitButton}
                    disabled={actionInFlight}
                    onClick={() => runAction(() => apiClient.api.workflowEventRequestsCancelCreate(Number(requestId)))}
                >
                    <Gt>Cancel request</Gt>
                </Button>
            )}

            {isManagerReview && data?.status === RequestStatusEnum.Pending && (
                <>
                    <Textarea
                        name="reject_reason"
                        initialValue={rejectReason}
                        onValueChange={v => setRejectReason(v as string)}
                        placeholder={t('Rejection reason')}
                    />
                    <div className={styles.actionButtons}>
                        <Button
                            icon={<Checked/>}
                            type={ButtonType.secondary}
                            color={ColorNames.CORAL_GREEN}
                            className={styles.actionButton}
                            disabled={actionInFlight}
                            onClick={() => runAction(() => apiClient.api.workflowEventRequestsApproveCreate(Number(requestId)))}
                        >
                            <Gt>Approve</Gt>
                        </Button>
                        <Button
                            icon={<Xcross/>}
                            type={ButtonType.secondary}
                            color={ColorNames.CORAL_FIRE}
                            className={styles.actionButton}
                            disabled={!rejectReason || actionInFlight}
                            onClick={() => runAction(() => apiClient.api.workflowEventRequestsRejectCreate(Number(requestId), {reason: rejectReason}))}
                        >
                            <Gt>Reject</Gt>
                        </Button>
                    </div>
                </>
            )}
        </Stack>
    )
}
